import type { Component } from 'vue'
import type { RouteLocationRaw, RouteParams, RouteRecordName } from 'vue-router'
import {
  BookOpenIcon,
  ChartColumnIcon,
  HouseIcon,
  LayersIcon,
  SparklesIcon,
  UploadIcon,
  WalletIcon
} from 'lucide-vue-next'
import type { Erp } from '@/interfaces/erp'

export interface NavItem {
  key: string
  label: string
  to: RouteLocationRaw
  /** Altre rotte per cui la voce risulta attiva (es. il dettaglio di un elenco). */
  activeFor?: string[]
}

export interface NavGroup {
  key: string
  label: string
  icon: Component
  /** Gruppo foglia: link diretto, senza sottovoci. */
  to?: RouteLocationRaw
  activeFor?: string[]
  items?: NavItem[]
  badge?: number
}

export interface NavigationContext {
  t: (key: string) => string
  isAdmin: boolean
  erps: Erp[]
  blockedImports: number
}

export type ActiveRoute = { name?: RouteRecordName | null; params: RouteParams }

const named = (name: string, params?: Record<string, string>): RouteLocationRaw =>
  params ? { name, params } : { name }

export function buildNavigation(ctx: NavigationContext): NavGroup[] {
  const { t } = ctx

  const erpItems: NavItem[] = ctx.erps
    .filter((erp): erp is Erp & { code: string } => Boolean(erp.code))
    .map((erp) => ({
      key: `erp:${erp.code}`,
      label: erp.name ?? erp.code,
      to: named('analyzeErpReport', { erp: erp.code })
    }))

  const groups: NavGroup[] = [
    { key: 'home', label: t('layout.menu.home'), icon: HouseIcon, to: named('home') },
    {
      key: 'import',
      label: t('layout.menu.import.main'),
      icon: UploadIcon,
      badge: ctx.blockedImports > 0 ? ctx.blockedImports : undefined,
      items: [
        { key: 'importFile', label: t('layout.menu.import.file'), to: named('importFile') },
        {
          key: 'importList',
          label: t('layout.menu.import.list'),
          to: named('importList'),
          activeFor: ['importDetail']
        },
        {
          key: 'importManualTransaction',
          label: t('layout.menu.import.manualTransaction'),
          to: named('importManualTransaction')
        },
        {
          key: 'portfolioManualEntry',
          label: t('layout.menu.import.portfolioManualEntry'),
          to: named('portfolioManualEntry')
        },
        {
          key: 'analyzeTransactionError',
          label: t('layout.menu.analyze.transactionError'),
          to: named('analyzeTransactionError')
        },
        { key: 'bankStatementImport', label: t('layout.menu.import.bankStatement'), to: named('bankStatementImport') }
      ]
    },
    {
      key: 'analyze',
      label: t('layout.menu.analyze.main'),
      icon: ChartColumnIcon,
      items: [
        {
          key: 'analyzeErpGenericReport',
          label: t('layout.menu.analyze.erpGenericReport'),
          to: named('analyzeErpGenericReport')
        },
        {
          key: 'analyzeSchedaContabile',
          label: t('layout.menu.analyze.schedaContabile'),
          to: named('analyzeSchedaContabile')
        },
        {
          key: 'analyzeProgressiveCalculation',
          label: t('layout.menu.analyze.progressiveCalculation'),
          to: named('analyzeProgressiveCalculation')
        },
        {
          key: 'analyzeExportCashActivityEur',
          label: t('layout.menu.analyze.exportCashActivityEur'),
          to: named('analyzeExportCashActivityEur')
        },
        {
          key: 'analyzeExportCashActivityNonEur',
          label: t('layout.menu.analyze.exportCashActivityNonEur'),
          to: named('analyzeExportCashActivityNonEur')
        },
        {
          key: 'portfolioPerformance',
          label: t('layout.menu.analyze.portfolioPerformance'),
          to: named('portfolioPerformance')
        },
        ...erpItems
      ]
    },
    {
      key: 'stock',
      label: t('layout.menu.stock.main'),
      icon: LayersIcon,
      items: [
        { key: 'analyzeStock', label: t('layout.menu.stock.stock'), to: named('analyzeStock') },
        {
          key: 'analyzeStockOpzioniTitoli',
          label: t('layout.menu.stock.stockOpzioniTitoli'),
          to: named('analyzeStockOpzioniTitoli')
        },
        { key: 'depositFunds', label: t('layout.menu.stock.depositFunds'), to: named('depositFunds') },
        { key: 'strumentiValutari', label: t('layout.menu.stock.strumentiValutari'), to: named('strumentiValutari') }
      ]
    },
    { key: 'accounts', label: t('layout.menu.accounts'), icon: WalletIcon, to: named('accounts') }
  ]

  if (ctx.isAdmin) {
    // Agent di analisi dati riservato agli admin; l'API impone comunque ROLE_ADMIN.
    groups.push({ key: 'agent', label: t('layout.menu.agent'), icon: SparklesIcon, to: named('agent') })
  }

  groups.push({
    key: 'documentation',
    label: t('layout.menu.documentation'),
    icon: BookOpenIcon,
    to: named('documentation'),
    activeFor: ['documentationDetail']
  })

  return groups
}

export function isNavTargetActive(
  target: { to: RouteLocationRaw; activeFor?: string[] },
  route: ActiveRoute
): boolean {
  if (typeof route.name !== 'string') return false
  if (target.activeFor?.includes(route.name)) return true
  if (typeof target.to !== 'object' || !('name' in target.to) || target.to.name !== route.name) return false

  const params = 'params' in target.to ? target.to.params : undefined
  if (!params) return true
  return Object.entries(params).every(([key, value]) => String(route.params[key]) === String(value))
}

export function findActiveGroupKey(groups: NavGroup[], route: ActiveRoute): string | null {
  const active = groups.find((group) =>
    group.to
      ? isNavTargetActive({ to: group.to, activeFor: group.activeFor }, route)
      : (group.items ?? []).some((item) => isNavTargetActive(item, route))
  )
  return active?.key ?? null
}
